import { getSafeLocalStorage } from '@/lib/jotai-storage';
import { logger } from '@/utils/logger/logger';
import { atom } from 'jotai';
// `jotai/vanilla/utils` is the same implementation as `jotai/utils`. Jest maps
// `jotai/utils` to a stand-in that drops the storage adapter, so importing the
// vanilla path lets tests exercise the real adapter without mocking Jotai.
import { atomWithStorage, createJSONStorage } from 'jotai/vanilla/utils';

export type SettingsModalState = {
  open: boolean;
  /** The matching `SettingsItem.href` from useSettingsList, e.g. `/settings/account`. */
  route: string;
  /** Optional in-page hash like `#about-you` (drives section scroll). */
  hash?: string;
};

export const settingsModalSourceAtom = atom<string | undefined>(undefined);

export const DEFAULT_SETTINGS_MODAL_ROUTE = '/settings/account';

/**
 * localStorage key for the persisted last-route. Exported so
 * `clearLogoutStorage` can drop the entry on full logout and prevent leaking
 * the previous user's last section on a shared browser.
 */
export const LAST_SETTINGS_ROUTE_STORAGE_KEY = 'heidi.settings-modal.last-route';

const reportedStorageOperations = new Set<string>();

const errorNameOf = (error: unknown): string => {
  const name = (error as { name?: unknown } | null)?.name;
  return typeof name === 'string' ? name : typeof error;
};

// A failed read or write keeps the in-memory route; only the error name is
// logged, once per operation. `subscribe` restores jotai's cross-tab listener,
// which it skips for any adapter that is not `window.localStorage` itself.
const lastRouteStorage = createJSONStorage<string>(() => ({
  ...getSafeLocalStorage((operation, _key, error) => {
    if (reportedStorageOperations.has(operation)) return;
    reportedStorageOperations.add(operation);
    logger.warn(`[settings] last route ${operation} failed`, {
      errorName: errorNameOf(error),
    });
  }),
  subscribe: (key: string, callback: (value: string | null) => void) => {
    const onStorage = (event: StorageEvent) => {
      if (event.storageArea === window.localStorage && event.key === key) {
        callback(event.newValue);
      }
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  },
}));

/**
 * Last route the user opened the settings modal to. Persisted across reloads
 * so reopening returns to the same place ("persistent dialog" UX). Cleared on
 * logout by `clearLogoutStorage` — that, plus the app's session-inactivity
 * timeout / token-expiry logout paths (which all funnel through
 * `clearLogoutStorage`), is sufficient lifecycle for a low-signal preference
 * like this one.
 *
 * Cross-tab updates (another tab writes to this key) reach this atom only
 * through `lastRouteStorage.subscribe`. Jotai skips its own `storage` listener
 * for this wrapped adapter, so removing that `subscribe` brings back stale tabs.
 */
export const lastSettingsRouteAtom = atomWithStorage<string>(
  LAST_SETTINGS_ROUTE_STORAGE_KEY,
  DEFAULT_SETTINGS_MODAL_ROUTE,
  lastRouteStorage
);

/**
 * Live state of the global settings modal (open/close + active section).
 *
 * Bidirectionally synced with the `?settings=<slug>` URL query param by
 * `useSettingsModalUrlSync` — the URL is treated as the source of truth on
 * external navigations (popstate, deep links, route changes), and atom
 * transitions write through to the URL via `history.pushState`/`replaceState`
 * (Next's router is intentionally bypassed to avoid re-rendering the page
 * tree behind the modal).
 */
export const settingsModalAtom = atom<SettingsModalState>({
  open: false,
  route: DEFAULT_SETTINGS_MODAL_ROUTE,
});

/**
 * The open flag alone, so subscribers that only care whether it's open don't
 * re-render on every route/hash change.
 */
export const isSettingsModalOpenAtom = atom((get) => get(settingsModalAtom).open);
